import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Search, Plus, Activity, Clock, Shield, LogOut, Sparkles, Users, ClipboardCheck,
  ArrowRight, Command as CommandIcon, FileSpreadsheet
} from 'lucide-react';
import { cn } from '../lib/utils';

// Quick command palette — uniwersalna nawigacja Cmd+K (Mac) / Ctrl+K (Win)
//
// Bez external deps (`cmdk` jest fajne ale doda 30kB). Własna implementacja,
// fuzzy match na label/keywords, keyboard navigation, focus trap, ESC close.
//
export default function CommandPalette({
  open, onOpenChange, page, setPage, isAdmin, onLogout
}) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Build commands list
  const commands = useMemo(() => {
    const base = [
      {
        id: 'goto-new',
        section: 'Nawigacja',
        label: 'Nowe zlecenie',
        keywords: 'new order upload plik dodaj',
        icon: Plus,
        shortcut: 'G N',
        action: () => setPage('new'),
      },
      {
        id: 'goto-live',
        section: 'Nawigacja',
        label: 'Live Dashboard',
        keywords: 'live dashboard real-time monitoring kolejka',
        icon: Activity,
        shortcut: 'G L',
        badge: 'NEW',
        action: () => setPage('live'),
      },
      {
        id: 'goto-history',
        section: 'Nawigacja',
        label: 'Historia zleceń',
        keywords: 'history orders archive',
        icon: Clock,
        shortcut: 'G H',
        action: () => setPage('history'),
      },
    ];
    if (isAdmin) {
      base.push({
        id: 'goto-clients',
        section: 'Nawigacja',
        label: 'Klienci',
        keywords: 'klienci tenants connections kanaly',
        icon: Users,
        shortcut: 'G K',
        action: () => setPage('clients'),
      });
      base.push({
        id: 'goto-readiness',
        section: 'Nawigacja',
        label: 'Gotowość',
        keywords: 'gotowosc listings readiness oferty',
        icon: ClipboardCheck,
        shortcut: 'G R',
        action: () => setPage('readiness'),
      });
      base.push({
        id: 'goto-admin',
        section: 'Nawigacja',
        label: 'Panel Admina',
        keywords: 'admin users credits',
        icon: Shield,
        shortcut: 'G A',
        action: () => setPage('admin'),
      });
    }

    base.push(
      {
        id: 'action-upload',
        section: 'Akcje',
        label: 'Wgraj nowy plik',
        keywords: 'upload plik xlsx csv',
        icon: FileSpreadsheet,
        action: () => { setPage('new'); setTimeout(() => document.querySelector('input[type=file]')?.click(), 100); },
      },
      {
        id: 'action-mailto',
        section: 'Pomoc',
        label: 'Kontakt do supportu',
        keywords: 'support help kontakt mediafy',
        icon: Sparkles,
        action: () => { window.location.href = 'mailto:kontakt@mediafy.com.pl'; },
      },
      {
        id: 'action-logout',
        section: 'Konto',
        label: 'Wyloguj się',
        keywords: 'logout sign out',
        icon: LogOut,
        action: onLogout,
      },
    );

    return base;
  }, [isAdmin, setPage, onLogout]);

  // Filter
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      (c.keywords || '').toLowerCase().includes(q)
    );
  }, [query, commands]);

  // Group by section
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(c => {
      const sec = c.section || 'Inne';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push(c);
    });
    return groups;
  }, [filtered]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset active index when filtering changes
  useEffect(() => { setActiveIdx(0); }, [query]);

  // Keyboard
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault(); onOpenChange(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = filtered[activeIdx];
        if (cmd) {
          cmd.action();
          onOpenChange(false);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, filtered, activeIdx, onOpenChange]);

  // Scroll active into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  let runningIdx = -1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-ink-950/40 backdrop-blur-sm animate-fade-in"
        onClick={() => onOpenChange(false)}
      />
      {/* Panel */}
      <div className="fixed left-1/2 top-[20vh] -translate-x-1/2 w-full max-w-xl px-4 z-[101] animate-scale-in origin-top">
        <div className="bg-white dark:bg-ink-900 rounded-xl shadow-xl border border-ink-200 dark:border-ink-800 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-ink-100 dark:border-ink-800">
            <Search className="w-5 h-5 text-ink-400 dark:text-ink-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj akcji, strony, lub wpisz polecenie…"
              className="flex-1 h-full bg-transparent outline-none text-base text-ink-900 dark:text-ink-50 placeholder:text-ink-400 dark:placeholder:text-ink-500"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-mono bg-ink-100 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 text-ink-500 dark:text-ink-400">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
            {filtered.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="mx-auto w-12 h-12 rounded-xl bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-400 dark:text-ink-500 mb-3">
                  <Search className="w-6 h-6" />
                </div>
                <div className="text-sm font-semibold text-ink-700 dark:text-ink-300">Brak wyników</div>
                <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">Spróbuj innej frazy</div>
              </div>
            ) : (
              Object.entries(grouped).map(([section, items]) => (
                <div key={section}>
                  <div className="px-4 pt-3 pb-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-ink-400 dark:text-ink-500">
                    {section}
                  </div>
                  {items.map(cmd => {
                    runningIdx += 1;
                    const idx = runningIdx;
                    const Icon = cmd.icon;
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={cmd.id}
                        type="button"
                        data-cmd-idx={idx}
                        onClick={() => { cmd.action(); onOpenChange(false); }}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left',
                          isActive
                            ? 'bg-brand-50 text-brand-900 dark:bg-brand-900/30 dark:text-brand-100'
                            : 'text-ink-800 hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-ink-800'
                        )}
                      >
                        <div className={cn(
                          'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
                          isActive
                            ? 'bg-brand-100 text-brand-700 dark:bg-brand-800/50 dark:text-brand-300'
                            : 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-400'
                        )}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="flex-1 font-medium">{cmd.label}</span>
                        {cmd.badge && (
                          <span className="px-1.5 py-0.5 rounded text-2xs font-bold uppercase tracking-wide bg-gradient-to-r from-brand-500 to-brand-700 text-white">
                            {cmd.badge}
                          </span>
                        )}
                        {cmd.shortcut && (
                          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-2xs font-mono bg-ink-100 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 text-ink-500 dark:text-ink-400">
                            {cmd.shortcut}
                          </kbd>
                        )}
                        {isActive && <ArrowRight className="w-4 h-4 text-brand-600 dark:text-brand-400 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 h-9 border-t border-ink-100 dark:border-ink-800 bg-ink-50/50 dark:bg-ink-800/30 text-2xs text-ink-500 dark:text-ink-400">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-700 font-mono">↑↓</kbd>
                nawigacja
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-700 font-mono">↵</kbd>
                wybierz
              </span>
            </div>
            <span className="inline-flex items-center gap-1">
              <CommandIcon className="w-3 h-3" />
              powered by MAMS
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
